import { useEffect, useRef, useCallback } from 'react';

interface ScrollPosition {
  x: number;
  y: number;
}

interface ScrollPositionOptions {
  key?: string;
  containerId?: string;
  storage?: 'session' | 'local';
}

export function useScrollPosition({
  key = 'scrollPosition',
  containerId,
  storage = 'session'
}: ScrollPositionOptions = {}) {
  const containerRef = useRef<HTMLElement | null>(null);
  const storageMethod = storage === 'session' ? sessionStorage : localStorage;
  const attemptRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (containerId) {
      containerRef.current = document.getElementById(containerId);
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [containerId]);

  const getScrollPosition = useCallback((): ScrollPosition => {
    if (containerRef.current) {
      return {
        x: containerRef.current.scrollLeft,
        y: containerRef.current.scrollTop
      };
    }
    return {
      x: window.scrollX,
      y: window.scrollY
    };
  }, []);

  const saveScrollPosition = useCallback(() => {
    const position = getScrollPosition();
    storageMethod.setItem(key, JSON.stringify(position));
    return position;
  }, [getScrollPosition, key, storageMethod]);

  const restoreScrollPosition = useCallback(() => {
    const savedPosition = storageMethod.getItem(key);
    if (!savedPosition) return null;

    try {
      const position: ScrollPosition = JSON.parse(savedPosition);
      
      // Use a more robust approach with exponential backoff
      const maxAttempts = 5;
      const baseDelay = 50; // Start with a 50ms delay

      const attemptRestore = () => {
        if (attemptRef.current >= maxAttempts) {
          attemptRef.current = 0;
          return;
        }

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        const scrollTarget = containerRef.current || window;
        const currentPos = getScrollPosition();

        // Only proceed if we're not already at the target position
        if (currentPos.y !== position.y) {
          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollTo(position.x, position.y);
            } else {
              window.scrollTo({
                left: position.x,
                top: position.y,
                behavior: 'instant' // Use instant to avoid smooth scrolling
              });
            }

            // Verify scroll position after a short delay
            timeoutRef.current = setTimeout(() => {
              const newPos = getScrollPosition();
              if (newPos.y !== position.y) {
                attemptRef.current++;
                // Exponential backoff
                const delay = baseDelay * Math.pow(2, attemptRef.current);
                timeoutRef.current = setTimeout(attemptRestore, delay);
              } else {
                attemptRef.current = 0;
              }
            }, 50);
          });
        }
      };

      // Initial attempt
      attemptRestore();
      return position;
    } catch (error) {
      console.error('Error restoring scroll position:', error);
      return null;
    }
  }, [key, storageMethod, getScrollPosition]);

  const clearScrollPosition = useCallback(() => {
    storageMethod.removeItem(key);
    attemptRef.current = 0;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [key, storageMethod]);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    clearScrollPosition,
    getScrollPosition
  };
}